/* istanbul ignore file */
/**
 * Created by rathompson on 11/5/21
 * File: ViewRoleModal
 * Project: MSP ©Palo Alto Networks
 * © Palo Alto Networks
 */

import { useMemo, useContext, useRef, useCallback, VFC, CSSProperties } from 'react'
import {
  Bold,
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  TruncateText,
  Body, ModalProps
} from '@panwds/react-ui'
import StringTemplate from '../../../../common/utils/StringTemplate'
import {
  LABEL_VIEW_INHERITED_TITLE,
  LABEL_NAME,
  LABEL_DESCRIPTION,
  LABEL_NO_DESCRIPTION,
  BUTTON_CLOSE
} from '../../../constants/LabelConstants'
import { getVarValue } from '../../../../common/utils/Sparky'
import { FF_ROLE_MODAL_PERMISSION_SETS, FF_SHOW_PERMISSIONS } from '../../../../common/utils/FeatureFlags'
import PermissionsTable from '../../common/permissionsTable/PermissionsTable'
import GlobalContext from '../../../../common/context/GlobalContext'
import type { IAM_Role } from '../../../../common/reducers/types/IAMReducer.types'
import type { IAM_Permission } from '../../../../common/reducers/types/IAMReducer.types'
import buildPermissionsTree from '../../../utils/buildPermissionTree'
import AddPermissionsTable from '../table/addPermissionsTable/AddPermissionsTable'

const showPermissionSets = getVarValue(FF_ROLE_MODAL_PERMISSION_SETS)
const showPermissions = !showPermissionSets && getVarValue(FF_SHOW_PERMISSIONS)

/**
 *   Role details modal
 */
const ViewRoleModal: VFC<ModalProps & {
  closeModal: () => void,
  role: IAM_Role
}> = ({ closeModal, isOpen, role }) => {

  const { iamReducer } = useContext(GlobalContext)
  const { permissionsMap = {} } = iamReducer

  const title = StringTemplate(LABEL_VIEW_INHERITED_TITLE, [role?.label || role?.name])

  const tableRef = useRef()

  const tableInstanceRef = useCallback((node) => {
    if (node) {
      tableRef.current = node
    }
  }, [])

  const permissions = useMemo(() => {
    const permissions: IAM_Permission[] = (role?.is_custom ? role?.aggregated_permissions : role?.permissions)?.map((permission) => permissionsMap[permission])
    return buildPermissionsTree(permissions)
  }, [permissionsMap, role?.aggregated_permissions, role?.is_custom, role?.permissions])

  const _role: IAM_Role = useMemo(() => {
    return {
      id: role?.name,
      name: role?.label,
      app_id: role?.app_id || '',
      aggregated_permissions: [...(role?.aggregated_permissions || [])],
      permissions: [...(role?.permissions || [])],
      permission_sets: role?.permission_sets,
      explicit_permissions: [...(role?.explicit_permissions || [])],
      read_only_permission_sets: role?.read_only_permission_sets,
      read_write_permission_sets: role?.read_write_permission_sets
    } as any
  }, [role])

  return (
    <Modal style={{ top: '50%', '--tw-translate-y': '-50%', zIndex: 1053 } as CSSProperties} size='lg' onClose={closeModal} isOpen={isOpen}>
      <div className='header-wrapper'>
        <ModalHeader title={role?.label || role?.name}/>
      </div>
      <ModalBody addClassName='tw-max-h-full tw-overflow-hidden'>
        <div className='tw-mt-2 tw-flex tw-flex-col'>
          <Bold>{LABEL_NAME}</Bold>
          <TruncateText>{role?.label || role?.name}</TruncateText>
        </div>
        <div className='tw-mt-2 tw-mb-2 tw-flex tw-flex-col'>
          <Bold>{LABEL_DESCRIPTION}</Bold>
          <Body size='lg'>{role?.description || LABEL_NO_DESCRIPTION}</Body>
        </div>
        {showPermissions && (
          <PermissionsTable
            data={permissions}
            enableRowSelect={false}
            enablePagination={true}
            tableRef={tableRef}
            tableInstanceRef={tableInstanceRef}
            expanded={false}
            tableState={{ expanded: {} }}
          />
        )}
        {showPermissionSets && (
          <AddPermissionsTable
            title={title as any}
            role={_role} setRole={() => null}
            setWebData={() => null}
            setAPIData={() => null}
            viewOnly
            useRoleData
            enableSearch
          />
        )}
      </ModalBody>
      <ModalFooter>
        <Button
          onClick={closeModal}
          appearance="secondary"
        >{BUTTON_CLOSE}</Button>
      </ModalFooter>
    </Modal>
  )
}

ViewRoleModal.propTypes = {}
export default ViewRoleModal
